import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <Layout>
      <SEOHead
        title="Terms of Service - NoQ5.ai"
        description="The Terms of Service that govern your use of NoQ5: account, subscription, acceptable use, intellectual property, liability, termination and governing law (Ireland)."
        canonicalUrl="https://noq5.ai/terms-of-service"
      />
      <article className="container px-4 md:px-6 py-12 max-w-3xl mx-auto prose prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary">
        <header className="not-prose mb-10">
          <p className="text-sm text-muted-foreground">Effective date: 25 April 2026 · Version 2.0</p>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-2">Terms of Service</h1>
          <p className="text-muted-foreground mt-3">
            These Terms of Service ("Terms") form a binding agreement between NoQ5.ai Technologies Ltd.
            ("NoQ5", "we") and the entity or person ordering the Service ("Customer", "you"). By creating
            an account, clicking "I accept", or using the Service you agree to these Terms.
          </p>
        </header>

        <h2>1. Service</h2>
        <p>
          NoQ5 provides a software-as-a-service platform that connects to your CRM (HubSpot, Salesforce,
          Pipedrive) and approved data sources (email, calendar, meeting tools) to draft, summarise and,
          subject to your approval, write CRM updates. Specific features are described at{' '}
          <Link to="/product/platform">noq5.ai/product/platform</Link>.
        </p>

        <h2>2. Accounts</h2>
        <p>
          You must be at least 18, provide accurate information, and keep your credentials confidential. You
          are responsible for all activity under your account. Notify us at{' '}
          <a href="mailto:batiste@noq5.ai">batiste@noq5.ai</a> of any unauthorised access.
        </p>

        <h2>3. Subscription, trial and fees</h2>
        <ul>
          <li>Plans, prices and trial terms are described at <Link to="/pricing">noq5.ai/pricing</Link>.</li>
          <li>Free trials end automatically; no credit card is required to start.</li>
          <li>Paid subscriptions renew for the same term unless cancelled at least 30 days before renewal.</li>
          <li>Fees are exclusive of VAT, due in advance and non-refundable except where required by law.</li>
          <li>We may change pricing on renewal with 60 days' notice.</li>
        </ul>

        <h2>4. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service to violate any law, third-party right, or our customers' rights;</li>
          <li>Reverse-engineer, decompile, or scrape the Service except as permitted by law;</li>
          <li>Use the Service to send spam, malware, or unlawful content;</li>
          <li>Submit personal data for which you do not have a lawful basis;</li>
          <li>Resell, sublicense or white-label the Service without a written agreement.</li>
        </ul>

        <h2>5. Customer data and privacy</h2>
        <p>
          You retain all rights in the data you submit ("Customer Data"). You grant NoQ5 a worldwide,
          non-exclusive licence to process Customer Data solely to provide the Service. Processing is
          governed by our <Link to="/privacy-policy">Privacy Policy</Link> and, for GDPR-regulated
          processing, our{' '}
          <Link to="/dpa">Data Processing Agreement</Link>. The list of subprocessors is at{' '}
          <Link to="/subprocessors">noq5.ai/subprocessors</Link>.
        </p>

        <h2>6. AI output</h2>
        <p>
          NoQ5 uses large language models to generate suggestions and drafts. Output may be inaccurate,
          incomplete or biased. You are responsible for reviewing and approving any output before it is
          written to your CRM or sent to third parties. NoQ5 disclaims warranties on AI output to the extent
          permitted by law.
        </p>

        <h2>7. Intellectual property</h2>
        <p>
          NoQ5 and its licensors own the Service, the underlying software, and all related IP. We grant you
          a limited, non-exclusive, non-transferable right to use the Service during the subscription term.
          Feedback you provide may be used by NoQ5 without restriction.
        </p>

        <h2>8. Confidentiality</h2>
        <p>
          Each party agrees to protect the other's confidential information with at least the same degree of
          care it uses for its own (and never less than reasonable care), and to use it only to perform under
          these Terms.
        </p>

        <h2>9. Warranties and disclaimers</h2>
        <p>
          NoQ5 warrants that the Service will materially conform to its documentation. EXCEPT AS EXPRESSLY
          STATED, THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, INCLUDING MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE OR NON-INFRINGEMENT.
        </p>

        <h2>10. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, neither party will be liable for indirect, incidental,
          special, consequential or punitive damages, lost profits, or loss of data. Each party's aggregate
          liability arising out of these Terms will not exceed the fees paid by Customer to NoQ5 in the 12
          months preceding the event giving rise to the claim. Nothing limits liability for fraud, gross
          negligence, wilful misconduct, or any liability that cannot be excluded by law.
        </p>

        <h2>11. Indemnification</h2>
        <p>
          NoQ5 will defend Customer against third-party claims that the Service infringes their IP rights,
          subject to standard exclusions. Customer will defend NoQ5 against claims arising from Customer
          Data or Customer's misuse of the Service.
        </p>

        <h2>12. Term and termination</h2>
        <p>
          These Terms continue until terminated. Either party may terminate for material breach not cured
          within 30 days of written notice. On termination we will delete or return Customer Data within 30
          days, subject to legal retention obligations.
        </p>

        <h2>13. Governing law and jurisdiction</h2>
        <p>
          These Terms are governed by the laws of Ireland. The courts of Dublin, Ireland have exclusive
          jurisdiction, without prejudice to mandatory consumer protections.
        </p>

        <h2>14. Changes</h2>
        <p>
          We may update these Terms; material changes will be communicated by email at least 14 days before
          they take effect. Continued use after the effective date constitutes acceptance.
        </p>

        <h2>15. Contact</h2>
        <p>
          NoQ5.ai Technologies Ltd. · Dublin, Ireland · <a href="mailto:batiste@noq5.ai">batiste@noq5.ai</a>
        </p>
      </article>
    </Layout>
  );
};

export default TermsOfService;
